import { Injectable } from '@angular/core';
import { NotificationsService } from 'api/notifications.api';
import { ReportingQueryService } from '../../services/reporting-query.service';
import { Transaction } from '@kempoweroyj/ngx-transactions-api-client';
import { GetVehiclesResponse, Vehicle, VehicleRecord, VehiclesApiService } from '@kempoweroyj/ngx-vehicles-api-client';
import { flatten, maxBy, mean } from 'lodash';
import { EMPTY, merge, Observable, of, Subject } from 'rxjs';
import { filter, map, publishReplay, refCount, startWith, switchMap, take, throttleTime } from 'rxjs/operators';
import { PopupService } from 'shared/popup-service/popup.service';

export interface VehicleRelatedInfo {
    stationIds: string[];
    idTokens: string[];
    identifiedAsEvs: string[];
    identifiedAsBatterySizekWh: number;
    latestSessions: Transaction[];
}

export const BatterySizeEstimateCache: {
    [vehicleId: string]: number;
} = {};

@Injectable({
    providedIn: 'root',
})
export class VehiclesService {
    private refreshRequested$ = new Subject<void>();
    constructor(
        private vehiclesApi: VehiclesApiService,
        private notifications: NotificationsService,
        private reportingQuery: ReportingQueryService,
        private popup: PopupService
    ) {}

    refresh() {
        this.refreshRequested$.next();
    }

    vehicleRecordByMAC$(mac: string, emitUndefinedIfNotFound = false): Observable<VehicleRecord> {
        return this.vehicleRecords$().pipe(
            map((vehicles) =>
                vehicles.find(
                    (v) =>
                        !!v.vehicle.chargingPorts?.find((p) => p?.macAddress.toLowerCase() === mac?.toLocaleLowerCase())
                )
            ),
            switchMap((vehicle) => {
                if (vehicle) {
                    return of(vehicle);
                } else if (emitUndefinedIfNotFound) {
                    return of(undefined);
                } else {
                    return EMPTY;
                }
            })
        );
    }

    vehicleRecord$(id: string, emitUndefinedIfNotFound = false): Observable<VehicleRecord> {
        return this.vehicleRecords$().pipe(
            map((vehicles) => vehicles.find((v) => v.vehicle.id === id)),
            switchMap((vehicle) => {
                if (vehicle) {
                    return of(vehicle);
                } else if (emitUndefinedIfNotFound) {
                    return of(undefined);
                } else {
                    return EMPTY;
                }
            })
        );
    }

    vehicleByMAC$(mac: string, emitUndefinedIfNotFound = false): Observable<Vehicle> {
        return this.vehicleRecordByMAC$(mac, emitUndefinedIfNotFound).pipe(map((v) => v?.vehicle));
    }

    vehicle$(id: string): Observable<Vehicle | undefined> {
        return this.vehicles$().pipe(map((vs) => vs.find((v) => v.id === id)));
    }

    getVehicle(id: string): Promise<Vehicle | undefined> {
        return this.vehicle$(id).pipe(take(1)).toPromise();
    }
    getVehicleByMac(evMacAddress: string): Promise<Vehicle | undefined> {
        return this.vehicleByMAC$(evMacAddress, true).pipe(take(1)).toPromise();
    }

    _vehicles$: Observable<VehicleRecord[]>;
    vehicleRecords$(): Observable<VehicleRecord[]> {
        if (!this._vehicles$) {
            const refresh$ = merge(
                this.refreshRequested$,
                this.notifications.notification$.pipe(
                    filter(
                        (n) =>
                            n.type === 'ChargingStationUpdated' &&
                            (n.changedSchemas?.includes('ev') ||
                                n.changedSchemas?.includes('evSchedule') ||
                                n.changedSchemas?.includes('v2icp'))
                    )
                )
            );
            this._vehicles$ = refresh$.pipe(
                startWith([]),
                throttleTime(2000, undefined, { leading: true, trailing: true }),
                switchMap(() =>
                    this.vehiclesApi.getVehicles().catch((err) => {
                        this.popup.error('Failed to fetch vehicles', { durationMs: 5000 });
                        console.error('Failed to fetch vehicles:', JSON.stringify(err));
                        return { vehicles: [] } as GetVehiclesResponse;
                    })
                ),
                filter((v) => !!v),
                map((res) => res.vehicles),
                publishReplay(1),
                refCount()
            );
        }

        return this._vehicles$;
    }

    vehicles$(): Observable<Vehicle[]> {
        return this.vehicleRecords$().pipe(map((vehicles) => vehicles.map((v) => v.vehicle)));
    }

    async getVehicleRelated(evMacAddresses: string[]): Promise<VehicleRelatedInfo> {
        // get stations
        const res = await this.reportingQuery.query(
            {
                query: {
                    indexName: 'schema-tx',
                    parameters: [{ term: { schema: 'tx' } }, { terms: { evMacAddress: evMacAddresses } }],
                    sort: { timestamp: { order: 'desc' } },
                    size: 5,
                    includeHits: true,
                    aggregations: {
                        stationIds: {
                            terms: {
                                field: 'stationId',
                                size: 10,
                            },
                        },
                        idTokens: {
                            terms: {
                                field: 'idToken',
                                size: 10,
                            },
                        },
                        evModels: {
                            terms: {
                                field: 'evIdentify.displayName',
                                size: 3,
                            },
                            aggs: {
                                hit: {
                                    top_hits: {
                                        size: 1,
                                        sort: { timestamp: 'desc' },
                                        _source: ['evIdentify'],
                                    },
                                },
                            },
                        },
                    },
                },
            },
            { src: 'ev-related-stations' }
        );

        // latest transactions
        const latestSessions = res.response.hits.map((h) => h._source);

        // stations
        const stationIds = Object.keys(res.response.stationIds);

        // id tokens
        const idTokens = Object.keys(res.response.idTokens);

        // EVs
        const identifiedAsEvs = Object.keys(res.response.evModels).filter((ev) => !ev.startsWith('Unidentified'));
        const identifiedAsBatterySizekWh: number =
            identifiedAsEvs.length > 0
                ? maxBy(
                      flatten(
                          identifiedAsEvs.map((evm) =>
                              [
                                  res.response.evModels[evm].aggs.hit.hits[0]?._source.evIdentify?.exactMatch
                                      ?.batteryCapacity?.net,
                                  ...(res.response.evModels[evm].aggs.hit.hits[0]?._source.evIdentify?.possibleEvs?.map(
                                      (p) => p.batteryCapacity?.net
                                  ) || []),
                              ].filter(Boolean)
                          )
                      )
                  )
                : undefined;

        return {
            idTokens,
            identifiedAsBatterySizekWh,
            identifiedAsEvs,
            latestSessions,
            stationIds,
        };
    }

    async getVehicleEstimatedBatterySizekWhByMac(evMacAddress: string) {
        const ev = await this.getVehicleByMac(evMacAddress);
        return this.getVehicleEstimatedBatterySizekWh(ev);
    }

    async getVehicleEstimatedBatterySizekWhById(evId: string) {
        const ev = await this.getVehicle(evId);
        return this.getVehicleEstimatedBatterySizekWh(ev);
    }

    async getVehicleEstimatedBatterySizekWh(ev: Vehicle) {
        if (!ev) {
            return undefined;
        }
        if (ev.fullChargeEnergykWh) {
            return ev.fullChargeEnergykWh;
        }

        const vehicleMacAddresses = ev.chargingPorts?.map((cp) => cp.macAddress);

        // only if mac address known
        if (!vehicleMacAddresses.length) {
            return undefined;
        }

        // check cache
        if (BatterySizeEstimateCache[ev.id]) {
            return BatterySizeEstimateCache[ev.id];
        }

        const info = await this.getVehicleRelated(vehicleMacAddresses);

        // use ev info if found
        if (info.identifiedAsBatterySizekWh) {
            BatterySizeEstimateCache[ev.id] = info.identifiedAsBatterySizekWh; // update cache;
            return info.identifiedAsBatterySizekWh;
        }

        // if not, find some largest TX's to estimate
        const estBatterySizekWh = await this.getVehicleEstimatedBatterySizekWhFromPastTransactions(vehicleMacAddresses);
        BatterySizeEstimateCache[ev.id] = estBatterySizekWh; // update cache;
        return estBatterySizekWh;
    }

    async getVehicleEstimatedBatterySizekWhFromPastTransactions(evMacAddresses: string[]) {
        // if not, find some largest TX's to estimate
        const txs = await this.reportingQuery.query(
            {
                query: {
                    indexName: 'schema-tx',
                    parameters: [
                        { term: { schema: 'tx' } },
                        { terms: { evMacAddress: evMacAddresses } },
                        { range: { socStart: { gte: 1 } } },
                        { range: { socEnd: { lte: 100 } } },
                    ],
                    sort: {
                        chargedEnergyKwh: 'desc',
                    },
                    source: ['socEnd', 'socStart', 'chargedEnergyKwh'],
                },
            },
            { src: 'ev-estimate-battery-kwh' }
        );

        // calculate battery size
        const batterySizeskWh = [];
        for (const tx_ of txs.response) {
            const tx: Transaction = tx_._source;
            const socDelta = tx.socEnd - tx.socStart;

            if (!(socDelta > 5) || !(tx.chargedEnergyKwh > 5)) {
                // sanity check.. ensure at least 5% and 5kWh charged
                continue;
            }
            const batterySizeEstimatekWh = (tx.chargedEnergyKwh / socDelta) * 100;
            batterySizeskWh.push(batterySizeEstimatekWh);
        }

        // get mean value
        if (batterySizeskWh.length > 0) {
            return Math.floor(mean(batterySizeskWh));
        }

        return undefined;
    }
}
